import React from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { routeConcept, listReviewAssignees, decideReview } from '../api';

// Stage 4: #HITL review. Routing, the decisions waiting on someone, and what was decided
// before. All three were separate sections of the old scroll; they are one job.
export default function ReviewStage({ concept: c, conceptId, onChanged }) {
  const { data: assigneeData } = useQuery({
    queryKey: ['training-review-assignees'], queryFn: listReviewAssignees,
  });
  const assignees = assigneeData?.assignees || {};
  const [routeAssignee, setRouteAssignee] = React.useState({}); // role -> user public_id ('' = auto)

  const route = useMutation({
    mutationFn: ({ to, assigneeId }) => routeConcept(conceptId, to, assigneeId),
    onSuccess: () => { toast.success('Routed'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Could not route this concept'),
  });
  const decide = useMutation({
    mutationFn: ({ reviewId, decision }) => decideReview(reviewId, { decision }),
    onSuccess: () => { toast.success('Decision recorded'); onChanged(); },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });

  const decided = (c.reviews || []).filter((r) => r.decision && r.decision !== 'pending');

  return (
    <div className="space-y-5">
      {(c.pending_reviews || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Awaiting decision</div>
          {c.pending_reviews.map((r) => (
            <div key={r.id} className="mb-3">
              <div className="text-xs text-[#c7d0e0] mb-1">
                {r.review_type.replace(/_/g, ' ')}
                {r.routed_by && <span className="text-[#7d8aa0]"> · routed by {r.routed_by}</span>}
                {r.due_at && (
                  <span className={r.breached_at ? 'text-[#ffb4b4]' : 'text-[#7d8aa0]'}>
                    {' '}· due {new Date(r.due_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => decide.mutate({ reviewId: r.id, decision: 'approved' })}
                  disabled={decide.isPending}
                  className="flex-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-600 text-[#04120a] disabled:opacity-40">
                  Approve
                </button>
                <button onClick={() => decide.mutate({ reviewId: r.id, decision: 'returned' })}
                  disabled={decide.isPending}
                  className="flex-1 text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] text-[#e6edf6] border border-[#2b3a4d] disabled:opacity-40">
                  Return
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">#HITL routing</div>
        <div className="flex flex-col gap-2 items-start">
          {(c.review_path || ['director']).map((role) => {
            // Latest review of this type (reviews are ordered oldest→newest).
            const rv = (c.reviews || []).filter((r) => r.review_type === role).slice(-1)[0];
            const decision = rv?.decision;
            if (decision === 'pending') {
              return (
                <span key={role} className="text-xs px-3 py-1.5 rounded-lg bg-[#12202f] text-[#8a95a8] border border-[#2b3a4d]">
                  Routed → {role.replace(/_/g, ' ')} · pending{rv?.routed_by ? ` (by ${rv.routed_by})` : ''}
                </span>
              );
            }
            if (decision === 'approved') {
              return (
                <span key={role} className="text-xs px-3 py-1.5 rounded-lg bg-[#0b2a17] text-green-400 border border-[#1c4a30]">
                  {role.replace(/_/g, ' ')} · approved
                </span>
              );
            }
            // Not yet routed, or returned/declined/changes required → allow (re-)routing,
            // optionally to a specific eligible reviewer instead of the first available.
            const opts = assignees[role] || [];
            return (
              <div key={role} className="flex items-center gap-2">
                {opts.length > 0 && (
                  <select
                    value={routeAssignee[role] || ''}
                    onChange={(e) => setRouteAssignee((a) => ({ ...a, [role]: e.target.value }))}
                    className="text-xs bg-[#0f1620] border border-[#3a4a5f] rounded px-2 py-1.5 text-[#e6ecf5] [&>option]:bg-[#0f1620]"
                  >
                    <option value="">Auto (first available)</option>
                    {opts.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                )}
                <button
                  onClick={() => route.mutate({ to: role, assigneeId: routeAssignee[role] })}
                  disabled={route.isPending}
                  className="text-xs px-3 py-1.5 rounded-lg bg-[#1f2a38] text-[#e6edf6] border border-[#2b3a4d] disabled:opacity-40"
                >
                  {decision ? 'Re-route' : 'Route'} → {role.replace(/_/g, ' ')}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {decided.length > 0 && (
        <div className="border-t border-[#1d2634] pt-4">
          <div className="text-[10px] uppercase tracking-wide text-[#7d8aa0] mb-2">Review history</div>
          {decided.map((r) => (
            <div key={r.id} className="text-[11px] text-[#c7d0e0] mb-1 flex items-center justify-between gap-2">
              <span>{r.review_type.replace(/_/g, ' ')} · {String(r.decision).replace(/_/g, ' ')}</span>
              <span className="text-[#7d8aa0] text-right">
                {r.reviewer ? `by ${r.reviewer}` : ''}{r.routed_by ? ` · routed by ${r.routed_by}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
